import { ComponentType } from 'react'
import loadable from '@loadable/component'

// Components
import {
  ImageInput as ImageInputComponent,
  Input as InputComponent,
  SearchSelect as SearchSelectComponent,
  Select as SelectComponent,
  Switch as SwitchComponent,
  TextArea as TextAreaComponent,
  Checkbox as CheckboxComponent,
  FileInput as FileInputComponent,
  Radio as RadioInputComponent,
  Toast,
} from '@tracklib/kit'
import { renderErrorMessage } from 'components/form/form'

// Utils
import { getInterceptors } from 'components/form/interceptors'
import { commonEmailMistakes } from '@constants'

// Loadable
const FormikLoadable = loadable.lib(() => import('formik'))

type FieldArrayProps = {
  name: string
  children: (arg0: {
    remove: (index: number) => void
    push: (emptyObj: Record<string, unknown>) => void
    ErrorMessage: () => React.ReactElement<typeof Toast | 'div'>
  }) => JSX.Element
}

export const FieldArray = ({ name, children }: FieldArrayProps): JSX.Element => {
  return (
    <FormikLoadable>
      {({ FieldArray: FormikFieldArray }) => (
        <FormikFieldArray
          name={name}
          render={({ remove, push, form: { errors, touched } }) => {
            return children({
              remove,
              push,

              ErrorMessage: () => renderErrorMessage(touched[name] && errors[name]),
            })
          }}
        />
      )}
    </FormikLoadable>
  )
}

const getOnChange =
  (method: (e: { target: { id: string; value: boolean | string | number | null } } | null | undefined) => void, name: string) => v => {
    // NOTE(Kitti): Normal inputs / react select and custom select all return different input values. Formik needs target.id to identify input
    if (!v) {
      method({ target: { id: name, value: null } })
    } else if (v.target) {
      if (v.target.type === 'checkbox') {
        method({ target: { id: name, value: v.target.checked } })
      } else {
        method({ target: { id: name, value: v.target.value } })
      }
    } else if (v.value) {
      method({ target: { id: name, value: v.value } })
    } else {
      method({ target: { id: name, value: v } })
    }
  }

const extractDeep = ({ defaultValue, data, name }) => {
  if (typeof defaultValue === 'string' || !name.includes('.')) {
    return defaultValue
  }
  let field = ''
  // (Kitti): rightsholders[0].ipi_number => ['rightsholders', '0', 'ipi_number]
  const [arrayName, index, paramKey, innerIndex, innerParamKey] = name.split(/[^0-9a-zA-Z_]+/)
  const dataArray = data[arrayName]
  if (dataArray && dataArray.length > +index) {
    if (innerIndex && innerParamKey) {
      const innerDataArray = dataArray[+index][paramKey]
      if (innerDataArray && innerDataArray.length > +innerIndex) {
        if (innerDataArray[+innerIndex]) {
          field = innerDataArray[+innerIndex][innerParamKey]
        }
      }
    } else if (dataArray[+index]) {
      field = dataArray[+index][paramKey]
    }
  }
  return field
}

const getFormikField = (Component: ComponentType<'input'>) => (props: Record<string, unknown>) =>
  (
    <FormikLoadable>
      {({ Field }) => (
        <Field {...props}>
          {({ field, form }) => {
            const { name, value, onChange, onBlur } = field
            const { type, error: customError } = props
            const touched = extractDeep({
              defaultValue: form.touched[name],
              data: form.touched,
              name,
            })
            const error =
              customError ||
              extractDeep({
                defaultValue: form.errors[name],
                data: form.errors,
                name,
              })
            return (
              <Component
                name={name}
                onChange={getOnChange(onChange, name)}
                onBlur={onBlur}
                {...props}
                warning={type === 'email' ? commonEmailMistakes(value) : ''}
                value={value || ''}
                touched={touched}
                error={error}
                {...getInterceptors(props.interceptor)}
              />
            )
          }}
        </Field>
      )}
    </FormikLoadable>
  )

export const ImageInput = getFormikField(ImageInputComponent)
export const Input = getFormikField(InputComponent)
export const SearchSelect = getFormikField(SearchSelectComponent)
export const Select = getFormikField(SelectComponent)
export const Switch = getFormikField(SwitchComponent)
export const TextArea = getFormikField(TextAreaComponent)
export const Checkbox = getFormikField(CheckboxComponent)
export const FileInput = getFormikField(FileInputComponent)
export const Radio = getFormikField(RadioInputComponent)
